/**
 * INDICADOR DE CONEXIÓN Y VENTAS PENDIENTES.
 *
 * Va en la barra de navegación, siempre visible. Es la respuesta permanente a la
 * pregunta que un cajero se hace cuando algo va raro: **"¿está guardando lo que
 * cobro?"**
 *
 * Tres estados, y ninguno alarma sin motivo:
 *
 *   · Todo bien y sin nada pendiente  → no se muestra nada. El silencio es la
 *     mejor señal de que funciona.
 *   · Sin conexión                    → aviso ámbar, con las ventas guardadas.
 *   · Hay pendientes o conflictos     → contador con acceso a la bandeja.
 */

import { Link } from "react-router-dom";
import { CloudUpload, RefreshCw, WifiOff } from "lucide-react";
import { useConexion } from "@/stores/conexion";
import { useOffline } from "@/stores/offline";
import { cx } from "./ui";

export function IndicadorConexion({ compacto = false }: { compacto?: boolean }) {
  const { enLinea } = useConexion();
  const { pendientes, clientes, conflictos, sincronizando, sincronizar } = useOffline();

  /*
    ⚠️ LOS CLIENTES CUENTAN IGUAL QUE LAS VENTAS.

    Un cliente registrado sin señal es lo que la venta de esa misma tarde va a
    nombrar. Dejándolo fuera del contador, el cajero vería «0 por enviar» con
    doña Carmen todavía guardada en el aparato — y creería que ya está todo
    arriba cuando lo que hay arriba es la mitad.
  */
  const porEnviar = pendientes + clientes;

  const hayAlgo = !enLinea || porEnviar > 0 || conflictos > 0;
  if (!hayAlgo) return null;

  // --- Sin conexión ---
  if (!enLinea) {
    return (
      <Link
        to="/ventas-sin-enviar"
        className={cx(
          "flex items-center gap-2 rounded-[var(--radius-control)] border border-warning/40 bg-warning-soft px-2.5 py-1.5",
          "text-xs font-medium text-warning transition-opacity hover:opacity-80",
        )}
        title={
          porEnviar > 0
            ? `Sin conexión. ${pendientes} venta(s) y ${clientes} cliente(s) guardados en este dispositivo.`
            : "Sin conexión. Puedes seguir vendiendo y registrando clientes."
        }
      >
        <WifiOff size={14} />
        {!compacto && <span>Sin conexión</span>}
        {porEnviar > 0 && (
          <span className="tabular rounded-full bg-warning px-1.5 text-[10px] font-bold text-white">
            {porEnviar}
          </span>
        )}
      </Link>
    );
  }

  // --- Con conexión, pero con cosas por enviar ---
  return (
    <div className="flex items-center gap-1">
      {porEnviar > 0 && (
        <button
          type="button"
          onClick={() => void sincronizar()}
          disabled={sincronizando}
          title={`${pendientes} venta(s) y ${clientes} cliente(s) por enviar. Toca para enviarlos ahora.`}
          className={cx(
            "flex items-center gap-2 rounded-[var(--radius-control)] border border-accent/40 bg-accent-soft px-2.5 py-1.5",
            "text-xs font-medium text-accent transition-opacity hover:opacity-80",
            "disabled:opacity-60",
          )}
        >
          {sincronizando ? (
            <RefreshCw size={14} className="animate-spin" />
          ) : (
            <CloudUpload size={14} />
          )}
          {!compacto && <span>{sincronizando ? "Enviando…" : "Por enviar"}</span>}
          <span className="tabular rounded-full bg-accent px-1.5 text-[10px] font-bold text-on-accent">
            {porEnviar}
          </span>
        </button>
      )}

      {conflictos > 0 && (
        <Link
          to="/ventas-sin-enviar"
          title={`${conflictos} venta(s) necesitan tu atención.`}
          className={cx(
            "flex items-center gap-1.5 rounded-[var(--radius-control)] border border-danger/40 bg-danger-soft px-2.5 py-1.5",
            "text-xs font-medium text-danger transition-opacity hover:opacity-80",
          )}
        >
          <span className="tabular">{conflictos}</span>
          {!compacto && <span>con problema</span>}
        </Link>
      )}
    </div>
  );
}
